import _axios from "@/axios";
import { reactive, shallowRef, triggerRef, watch } from "@vue/composition-api";
import { showSnackBar } from "@/composables/snackbar";
import lodash from "lodash";

export function useTranslation() {
  const translationData = reactive({
    isLoading: false,
    searchTerm: "",
    hasSearchResult: false,
  });

  const currentTranslation = shallowRef({});
  const currentTranslationBase = shallowRef({});
  const currentTranslationOverride = shallowRef({});
  const currentTranslationChanged = shallowRef({});

  watch(
    () => translationData.searchTerm,
    function (newValue, oldValue) {
      if (newValue != "" && newValue != oldValue) {
        translationData.hasSearchResult = false;
      }
    }
  );

  const matchSearchTerm = (key, lang) => {
    const str = currentTranslation.value[lang][key];

    if (
      str.toLowerCase().indexOf(translationData.searchTerm.toLowerCase()) >
        -1 ||
      key.toLowerCase().indexOf(translationData.searchTerm.toLowerCase()) > -1
    ) {
      translationData.hasSearchResult = true;

      return true;
    } else {
      return false;
    }
  };
  const isCurrentFieldUnsaved = (lang, key) => {
    return (
      currentTranslationChanged.value != null &&
      currentTranslationChanged.value[lang] != null &&
      currentTranslationChanged.value[lang][key] != null &&
      currentTranslationChanged.value[lang][key] == true
    );
  };

  const changeTranslation = (lang, key) => {
    const obj = {};
    obj[lang] = {};
    obj[lang][key] = true;
    currentTranslationChanged.value = lodash.merge(
      currentTranslationChanged.value,
      obj
    );

    triggerRef(currentTranslationChanged);
  };

  function unflatten(data) {
    "use strict";
    if (Object(data) !== data || Array.isArray(data)) return data;
    var regex = /\.?([^.[\]]+)|\[(\d+)\]/g,
      resultholder = {};
    for (var p in data) {
      var cur = resultholder,
        prop = "",
        m;
      while ((m = regex.exec(p))) {
        cur = cur[prop] || (cur[prop] = m[2] ? [] : {});
        prop = m[2] || m[1];
      }
      cur[prop] = data[p];
    }
    return resultholder[""] || resultholder;
  }

  function flatten(data) {
    if (null == data) {
      return;
    }
    data = JSON.parse(data);

    var result = {};
    function recurse(cur, prop) {
      if (Object(cur) !== cur) {
        result[prop] = cur;
      } else if (Array.isArray(cur)) {
        for (var i = 0, l = cur.length; i < l; i++)
          recurse(cur[i], prop + "[" + i + "]");
        if (l == 0) result[prop] = [];
      } else {
        var isEmpty = true;
        for (var p in cur) {
          isEmpty = false;
          recurse(cur[p], prop ? prop + "." + p : p);
        }
        if (isEmpty && prop) result[prop] = {};
      }
    }
    recurse(data, "");

    return result;
  }

  function updateClientPortalTranslation(
    translationContent,
    clientPortalId,
    lang
  ) {
    return _axios
      .put("/update-client-portal-translation/" + clientPortalId + "/" + lang, {
        translation: JSON.stringify(unflatten(translationContent[lang])),
      })
      .then(() => {
        showSnackBar();
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function getClientPortalTranslation(clientPortalId, lang) {
    return _axios
      .get("/client-portal-translation/" + clientPortalId + "/" + lang)
      .then((response) => {
        const flattenTranslationBaseContent = flatten(
          response.data.data.translationContent
        );
        const flattenTranslationContent = flatten(
          response.data.data.translationContent
        );
        const flattenTranslationContentOverride = flatten(
          response.data.data.translationContentOverride
        );

        currentTranslationBase.value[lang] = flattenTranslationBaseContent;
        currentTranslationOverride.value[
          lang
        ] = flattenTranslationContentOverride;

        lodash.merge(
          flattenTranslationContent,
          flattenTranslationContentOverride
        );

        currentTranslation.value[lang] = flattenTranslationContent;

        triggerRef(currentTranslation);
        triggerRef(currentTranslationBase);
        triggerRef(currentTranslationOverride);
      })
      .finally(() => {
        translationData.isLoading = false;
      });
  }

  function removeClientPortalTranslation(clientPortalId, lang) {
    return _axios
      .delete(
        "/remove-client-portal-translation/" + clientPortalId + "/" + lang
      )
      .then(() => {
        currentTranslation.value = currentTranslationBase.value;
        currentTranslationOverride.value = {};
        triggerRef(currentTranslation);
        triggerRef(currentTranslationBase);
        triggerRef(currentTranslationOverride);
        showSnackBar();
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function updateGroupTranslation(translationContent, groupId, lang) {
    return _axios
      .put("/update-group-translation/" + groupId + "/" + lang, {
        translation: JSON.stringify(unflatten(translationContent[lang])),
      })
      .then(() => {
        showSnackBar();
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function getGroupTranslation(groupId, lang) {
    return _axios
      .get("/group-translation/" + groupId + "/" + lang)
      .then((response) => {
        const flattenTranslationBaseContent = flatten(
          response.data.data.translationContent
        );
        const flattenTranslationContent = flatten(
          response.data.data.translationContent
        );
        const flattenTranslationContentOverride = flatten(
          response.data.data.translationContentOverride
        );

        currentTranslationBase.value[lang] = flattenTranslationBaseContent;
        currentTranslationOverride.value[
          lang
        ] = flattenTranslationContentOverride;

        lodash.merge(
          flattenTranslationContent,
          flattenTranslationContentOverride
        );

        currentTranslation.value[lang] = flattenTranslationContent;

        triggerRef(currentTranslation);
        triggerRef(currentTranslationBase);
        triggerRef(currentTranslationOverride);
      })
      .finally(() => {
        translationData.isLoading = false;
      });
  }

  function removeGroupTranslation(groupId, lang) {
    return _axios
      .delete("/remove-group-translation/" + groupId + "/" + lang)
      .then(() => {
        currentTranslation.value = currentTranslationBase.value;
        currentTranslationOverride.value = {};
        triggerRef(currentTranslation);
        triggerRef(currentTranslationBase);
        triggerRef(currentTranslationOverride);
        showSnackBar();
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function updatePortalModelTranslation(
    translationContent,
    portalModelId,
    lang
  ) {
    return _axios
      .put("/update-portal-model-translation/" + portalModelId + "/" + lang, {
        translation: translationContent[lang],
      })
      .then(() => {
        showSnackBar();
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  function getPortalModelTranslation(portalModelId, lang) {
    return _axios
      .get("/portal-model-translation/" + portalModelId + "/" + lang)
      .then((response) => {
        // ugly hack to trigger reactivity

        currentTranslation.value[lang] = response.data.data.translationContent;
        triggerRef(currentTranslation);
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      });
  }

  return {
    matchSearchTerm,
    isCurrentFieldUnsaved,
    changeTranslation,
    translationData,
    currentTranslationChanged,
    currentTranslationBase,
    currentTranslation,
    currentTranslationOverride,
    updateClientPortalTranslation,
    getClientPortalTranslation,
    removeClientPortalTranslation,
    updateGroupTranslation,
    getGroupTranslation,
    removeGroupTranslation,
    updatePortalModelTranslation,
    getPortalModelTranslation,
  };
}
